import { Divider, Stack } from "@mui/material";
import { useContext, useState, useEffect, useCallback } from "react";
import { useRevalidator } from "react-router-dom";

import DataContext from "../../../../../../../../../context/DataContext";
import useMedicalCompleteLock from "../../../../../../../../../hooks/useMedicalCompleteLock";
import useService from "../../../../../../../../../hooks/useService";
import Colors from "../../../../../../../../global/Colors";
import FullyRoundedButton from "../../../../../../../../ui library/components/buttons/FullyRoundedButton";
import HyperLinkButton from "../../../../../../../../ui library/components/buttons/HyperLinkButton";
import { useProCliStore } from "../../hooks/useProCliStore";
import AnalyticsStatus from "../../ProviderAnalytics/components/AnalyticsStatus";
import Balances from "../../ProviderAnalytics/components/Balances";
import Status from "../../ProviderAnalytics/components/Status";
import ProviderDetailsContainer from "../ProviderDetailsContainer";
import { STRINGS } from "../utils/strings";

export default function ProviderListDetailsv2({ accordionItemContent }) {
  const { userRoles } = useContext(DataContext);
  const { providersData, setProvidersData } = useProCliStore();
  const providerhistoryRepository = useService(
    "repositories/provider-client-status-history-repository"
  );
  const providerClientStatusRepository = useService("repositories/provider-client-status-repository");
  const [providerHistory, setProviderHistory] = useState(null);

  const getHistory = useCallback(async (providerClientStatusId) => {
    try {
      const historyResponse = await providerhistoryRepository.query({ providerClientStatusId });
      const historyData = Array.isArray(historyResponse) ? historyResponse[0] : historyResponse;
      setProviderHistory(historyData);
    } catch (error) {
      console.error("Error fetching provider history:", error);
      setProviderHistory(null);
    }
  }, [providerhistoryRepository]);

  useEffect(() => {
    const providerClientStatusId = accordionItemContent?.proCliData?.id;
    if (providerClientStatusId) {
      getHistory(providerClientStatusId);
    }
  }, [accordionItemContent?.proCliData?.id, getHistory]);

  const index = accordionItemContent.index;
  const providerInfo = accordionItemContent.proCliData.provider;
  const caseKey = accordionItemContent.proCliData.id;
  const { isProviderRecordLocked } = useMedicalCompleteLock();
  const recordLocked = isProviderRecordLocked(accordionItemContent.proCliData);

  const [saveBtn, disableSaveBtn] = useState(true);
  const [clearBtn, disableClearBtn] = useState(true);
  const [refreshDetails, setRefreshDetails] = useState(false);
  const { revalidate } = useRevalidator();

  const activateButtons = (save = false, clear = false) => {
    disableSaveBtn(save);
    disableClearBtn(clear);
  };

  const deactivateButtons = (save = true, clear = true) => {
    disableSaveBtn(save);
    disableClearBtn(clear);
  };

  const handleOnClickViewProvider = () => {
    accordionItemContent.setProviderData(providerInfo);
    accordionItemContent.setOpenProvDetail(true);
  };

  const handleOnClickDeleteProvider = () => {
    accordionItemContent.setProviderData(accordionItemContent.proCliData);
    accordionItemContent.setOpenDeleteModal(true);
  };

  const handleOnClickSave = async () => {
    try {
      /*Note: accordionItemContent will have editebleFields as null. Therefore, we are fetching directly from state because OnChange from textfield is reflected in the state. */
      const editableData = providersData.get(index).editableFields;
      const editableFieldsKeyMapping = STRINGS.EDITABLE_FIELDS_MAPPING;
      const patchData = Object.keys(editableData).reduce((acc, key) => {
        const mappedKey = editableFieldsKeyMapping[key];
        if (mappedKey) {
          acc[mappedKey] = editableData[key];
        }
        return acc;
      }, {});

      await providerClientStatusRepository.patch(caseKey, patchData);
      accordionItemContent.handleSuccess("Provider Client details successfully updated.");
      deactivateButtons();
      revalidate();
    } catch (error) {
      accordionItemContent.handleError(error);
    }
  };

  const handleOnClickClear = () => {
    try {
      const updatedProvidersData = new Map(providersData);
      updatedProvidersData.set(index, {
        ...updatedProvidersData.get(index),
        editableFields: {},
      });

      setProvidersData(updatedProvidersData);
      disableSaveBtn(true);
      disableClearBtn(true);
      setRefreshDetails(!refreshDetails);
    } catch (error) {
      accordionItemContent.handleError(error);
    }
  };

  return (
    <div className="my-4">
      <Stack spacing={1} direction="column" className="h-full">
        <ProviderDetailsContainer title={"Status"}>
          <AnalyticsStatus
            key={refreshDetails}
            currentAccordionItem={accordionItemContent}
            activateButtons={activateButtons}
            deactivateButtons={deactivateButtons}
          />
        </ProviderDetailsContainer>
        <Stack spacing={1.5} direction="row" className="mx-2 py-2 pb-4">
          <ProviderDetailsContainer title={"Balances"}>
            <Balances
              key={refreshDetails}
              currentAccordionItem={accordionItemContent}
              activateButtons={activateButtons}
              deactivateButtons={deactivateButtons}
              providerHistory={providerHistory}
            />
          </ProviderDetailsContainer>
          <ProviderDetailsContainer title={"Statuses"}>
            <Status
              key={refreshDetails}
              currentAccordionItem={accordionItemContent}
              activateButtons={activateButtons}
              deactivateButtons={deactivateButtons}
              providerHistory={providerHistory}
            />
          </ProviderDetailsContainer>
        </Stack>
      </Stack>
      <div className="m-2 flex flex-row items-baseline justify-between">
        <Stack spacing={2} direction="row">
          <FullyRoundedButton
            buttonText="Save"
            disabled={saveBtn || recordLocked}
            horizontalPadding="px-12"
            verticalPadding="py-2"
            handleOnClick={handleOnClickSave}
            hoverBgColor={Colors.blue[600]}
          />
          <FullyRoundedButton
            variant="outlined"
            disabled={clearBtn}
            bgColor="bg-white"
            hoverBgColor={Colors.blue[100]}
            textColor="text-blue-600"
            hoverTextColor="text-blue-600"
            borderWidth="border-[1px]"
            hoverBorderWidth="border-[1px]"
            borderColor="border-blue-600"
            buttonText="Clear"
            horizontalPadding="px-12"
            verticalPadding="py-2"
            handleOnClick={handleOnClickClear}
          />
        </Stack>
        <Stack spacing={1} direction="row">
          <HyperLinkButton
            buttonText={"View Provider Details"}
            buttonTextSize="text-xs"
            handleOnClick={handleOnClickViewProvider}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ color: Colors.black, borderColor: Colors.black }}
          />
          <HyperLinkButton
            buttonText={"Delete Provider Connection"}
            buttonTextSize="text-xs"
            handleOnClick={handleOnClickDeleteProvider}
            disabled={
              recordLocked ||
              !userRoles.permissions.includes("provider.delete_providerclientstatus")
            }
          />
        </Stack>
      </div>
    </div>
  );
}
